import {useEffect,useRef,useState,type PointerEvent as ReactPointerEvent} from "react";
import {FileImage,FileSpreadsheet,FileText} from "lucide-react";
import {api,type MaintenanceAttachment} from "./api";
import {sanitizeDocumentHtml} from "./safe-document-html";
import {removeEmptyPreviewColumns} from "./spreadsheet-preview";

type ThumbnailState=
  |{kind:"loading"}
  |{kind:"image";url:string}
  |{kind:"pdf";url:string}
  |{kind:"document";html:string}
  |{kind:"sheet";rows:string[][]}
  |{kind:"fallback"};

type FullPreviewState=
  |{kind:"loading"}
  |{kind:"image";url:string}
  |{kind:"pdf";url:string}
  |{kind:"word"}
  |{kind:"document";html:string}
  |{kind:"sheet";rows:string[][]}
  |{kind:"text";content:string}
  |{kind:"fallback"};

export function AttachmentThumbnail({maintenanceId,attachment,file}:{maintenanceId?:string;attachment?:MaintenanceAttachment;file?:File}){
  const [state,setState]=useState<ThumbnailState>({kind:"loading"});
  const fileName=file?.name||attachment?.file_name||"";
  const mimeType=file?.type||attachment?.mime_type||"application/octet-stream";

  useEffect(()=>{
    let active=true,objectUrl="";
    void (async()=>{
      try{
        if(!file&&(!maintenanceId||!attachment))throw new Error("ANEXO_INDISPONÍVEL");
        const blob=file||await api.maintenanceAttachmentContent(maintenanceId!,attachment!.id);
        if(!active)return;
        const name=fileName.toLocaleLowerCase("pt-BR");
        if(mimeType.startsWith("image/")){
          objectUrl=URL.createObjectURL(blob);
          return setState({kind:"image",url:objectUrl});
        }
        if(mimeType==="application/pdf"){
          const pdfjs=await import("pdfjs-dist");
          const workerUrl=(await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
          pdfjs.GlobalWorkerOptions.workerSrc=workerUrl;
          const document=await pdfjs.getDocument({data:await blob.arrayBuffer()}).promise;
          const page=await document.getPage(1);
          const baseViewport=page.getViewport({scale:1});
          const viewport=page.getViewport({scale:220/baseViewport.width});
          const canvas=window.document.createElement("canvas");
          canvas.width=Math.ceil(viewport.width);canvas.height=Math.ceil(viewport.height);
          const context=canvas.getContext("2d");
          if(!context)throw new Error("CANVAS_INDISPONÍVEL");
          await page.render({canvas,canvasContext:context,viewport}).promise;
          if(active)setState({kind:"pdf",url:canvas.toDataURL("image/jpeg",.8)});
          return;
        }
        if(name.endsWith(".docx")){
          const mammoth=await import("mammoth");
          const result=await mammoth.convertToHtml({arrayBuffer:await blob.arrayBuffer()});
          if(active)setState({kind:"document",html:sanitizeDocumentHtml(result.value||"<p>DOCUMENTO SEM CONTEÚDO VISÍVEL.</p>")});
          return;
        }
        if(name.endsWith(".xlsx")||name.endsWith(".xlsm")||name.endsWith(".xls")){
          const XLSX=await import("xlsx");
          const workbook=XLSX.read(await blob.arrayBuffer(),{type:"array"});
          const sheet=workbook.Sheets[workbook.SheetNames[0]];
          const sourceRows=XLSX.utils.sheet_to_json<unknown[]>(sheet,{header:1,raw:false,defval:""}).slice(0,4).map(row=>row.map(value=>String(value)));
          const rows=removeEmptyPreviewColumns(sourceRows).map(row=>row.slice(0,4));
          if(active)setState({kind:"sheet",rows});
          return;
        }
        if(active)setState({kind:"fallback"});
      }catch{
        if(active)setState({kind:"fallback"});
      }
    })();
    return()=>{active=false;if(objectUrl)URL.revokeObjectURL(objectUrl)};
  },[attachment?.id,file,fileName,maintenanceId,mimeType]);

  if(state.kind==="image"||state.kind==="pdf")return <div className={`attachment-thumbnail ${state.kind}`}><img src={state.url} alt=""/></div>;
  if(state.kind==="document")return <div className="attachment-thumbnail document"><div dangerouslySetInnerHTML={{__html:state.html}}/></div>;
  if(state.kind==="sheet")return <div className="attachment-thumbnail sheet"><FileSpreadsheet/><table><tbody>{state.rows.map((row,rowIndex)=><tr key={rowIndex}>{row.map((cell,columnIndex)=><td key={columnIndex}>{cell}</td>)}</tr>)}</tbody></table></div>;
  if(state.kind==="loading")return <div className="attachment-thumbnail loading" aria-label="Carregando miniatura"><span/></div>;
  return <div className="attachment-thumbnail fallback">{mimeType.startsWith("image/")?<FileImage/>:<FileText/>}</div>;
}

export function AttachmentFullPreview({file}:{file:File}){
  const [state,setState]=useState<FullPreviewState>({kind:"loading"});
  const [wordDocument,setWordDocument]=useState<ArrayBuffer>();
  const wordContainerRef=useRef<HTMLDivElement>(null);
  const sheetDragRef=useRef<{x:number;left:number}|null>(null);
  const beginSheetDrag=(event:ReactPointerEvent<HTMLDivElement>)=>{
    if(event.button!==0)return;
    sheetDragRef.current={x:event.clientX,left:event.currentTarget.scrollLeft};
    event.currentTarget.setPointerCapture(event.pointerId);
    event.currentTarget.classList.add("dragging");
  };
  const moveSheetDrag=(event:ReactPointerEvent<HTMLDivElement>)=>{
    const drag=sheetDragRef.current;if(!drag)return;
    event.currentTarget.scrollLeft=drag.left-(event.clientX-drag.x);
  };
  const endSheetDrag=(event:ReactPointerEvent<HTMLDivElement>)=>{
    sheetDragRef.current=null;
    event.currentTarget.classList.remove("dragging");
  };

  useEffect(()=>{
    let active=true,objectUrl="";
    void (async()=>{
      try{
        const name=file.name.toLocaleLowerCase("pt-BR");
        if(file.type.startsWith("image/")){
          objectUrl=URL.createObjectURL(file);
          if(active)setState({kind:"image",url:objectUrl});
          return;
        }
        if(file.type==="application/pdf"||name.endsWith(".pdf")){
          objectUrl=URL.createObjectURL(file);
          if(active)setState({kind:"pdf",url:objectUrl});
          return;
        }
        if(name.endsWith(".docx")){
          if(active){setWordDocument(await file.arrayBuffer());setState({kind:"word"});}
          return;
        }
        if(name.endsWith(".xlsx")||name.endsWith(".xlsm")||name.endsWith(".xls")){
          const XLSX=await import("xlsx");
          const workbook=XLSX.read(await file.arrayBuffer(),{type:"array"});
          const sheet=workbook.Sheets[workbook.SheetNames[0]];
          const rows=removeEmptyPreviewColumns(XLSX.utils.sheet_to_json<unknown[]>(sheet,{header:1,raw:false,defval:""}).map(row=>row.map(value=>String(value))));
          if(active)setState({kind:"sheet",rows});
          return;
        }
        if(name.endsWith(".txt")||name.endsWith(".csv")){
          if(active)setState({kind:"text",content:await file.text()});
          return;
        }
        if(active)setState({kind:"fallback"});
      }catch{
        if(active)setState({kind:"fallback"});
      }
    })();
    return()=>{active=false;if(objectUrl)URL.revokeObjectURL(objectUrl)};
  },[file]);

  useEffect(()=>{
    if(state.kind!=="word"||!wordDocument||!wordContainerRef.current)return;
    let active=true;
    const container=wordContainerRef.current;
    container.replaceChildren();
    void (async()=>{
      try{
        const {renderAsync}=await import("docx-preview");
        await renderAsync(wordDocument,container,undefined,{inWrapper:true,ignoreWidth:false,ignoreHeight:false,renderHeaders:true,renderFooters:true,renderFootnotes:true,useBase64URL:true});
        container.querySelectorAll("table").forEach(table=>{
          table.style.setProperty("display","table","important");
          table.style.setProperty("margin-left","auto","important");
          table.style.setProperty("margin-right","auto","important");
        });
      }catch{
        if(active)setState({kind:"fallback"});
      }
    })();
    return()=>{active=false;container.replaceChildren()};
  },[state.kind,wordDocument]);

  if(state.kind==="loading")return <div className="attachment-full-preview loading" aria-label="Carregando documento"><span/></div>;
  if(state.kind==="image")return <div className="attachment-full-preview image"><img src={state.url} alt={file.name}/></div>;
  if(state.kind==="pdf")return <div className="attachment-full-preview pdf"><iframe title={file.name} src={state.url}/></div>;
  if(state.kind==="word")return <div ref={wordContainerRef} className="attachment-full-preview word" aria-label={`Documento Word: ${file.name}`}/>;
  if(state.kind==="document")return <article className="attachment-full-preview document" dangerouslySetInnerHTML={{__html:state.html}}/>;
  if(state.kind==="sheet")return <div className="attachment-full-preview sheet" onPointerDown={beginSheetDrag} onPointerMove={moveSheetDrag} onPointerUp={endSheetDrag} onPointerCancel={endSheetDrag}><table><tbody>{state.rows.map((row,rowIndex)=><tr key={rowIndex}>{row.map((cell,columnIndex)=><td key={columnIndex}>{cell}</td>)}</tr>)}</tbody></table></div>;
  if(state.kind==="text")return <pre className="attachment-full-preview text">{state.content}</pre>;
  return <div className="attachment-full-preview fallback"><FileText/><p>Não foi possível pré-visualizar este formato.</p></div>;
}
